import { useAuth } from "@/lib/auth";
import { CommunicationProvider, useCommunication } from "@/lib/CommunicationWrapper"
import { CLOUDFLARE_URL, SUPABASE_URL } from "@/lib/config";
import { Sparkles, Calendar, BookOpen, Zap, ArrowRight, Star } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Comparison } from "./Comparison";
import { useSearchParams } from "react-router-dom";

import startguyUrl from "../../../images/startguy.png"
import extlogoUrl from "../../../images/extlogo.png";

// Convert to absolute URLs for iframe with base tag
const startguy = new URL(startguyUrl, import.meta.url).href;
const extlogo = new URL(extlogoUrl, import.meta.url).href;

const highlights = {
    "auto_rotate_theme": "automatic theme rotation",
    "assignments_upsert": "unlimited planning",
    "assignments_auto_schedule": "assignment auto-plan",
    "assignments_due_view": "unlimited assignment planning"
}

export const ClaimTrialContent = ({ highlight }: { highlight: string | null }) => {

    const { token } = useAuth();
    const { trackEvent, closeModal, updateOptions, getOptions } = useCommunication();

    const scrollToRef = useRef<HTMLDivElement>(null);

    const features = [
        { icon: Calendar, text: 'Full calendar sync', color: 'from-blue-500 to-cyan-500' },
        { icon: BookOpen, text: 'Unlimited notes', color: 'from-purple-500 to-pink-500' },
        { icon: Zap, text: 'Get instant lecture notes', color: 'from-orange-500 to-red-500' },
    ]

    const acceptTrial = async () => {

        try {
            const response = await fetch(`${CLOUDFLARE_URL}/trial/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }
            });

            const { message, error } = await response.json();

            console.log("[acceptTrial] message", message);
            console.log("[acceptTrial] error", error);

            if (error) {
                trackEvent("signup_claim_trial_failed", { "distinct_id": uuid, "error": response.statusText });
            } else if (message?.status === "success") {
                const { planType, planEnd, planStart, usedTrial } = message.subscriptionState;
                const options = getOptions();
                const updatedAuth = {
                    ...options.auth,
                    "user": {
                        ...options.auth.user,
                        "app_metadata": {
                            ...options.auth.user.app_metadata,
                            "plan_type": planType,
                            "plan_end": planEnd,
                            "plan_start": planStart,
                            "used_trial": usedTrial,
                        }
                    }
                };
                updateOptions({ "auth": updatedAuth });
            }
        } catch (error) {
            console.error("[acceptTrial] error", error);
        }
    }

    const scrollToComparison = () => {
        if (scrollToRef.current) {
            scrollToRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }

    return (
        <div className="flex relative w-full h-full">
            <img src={extlogo} className="absolute top-8 right-8 size-6 object-cover z-4" />


            {/* Main Content */}
            <div className="w-full h-full">
                <div className="relative w-full h-[500px] overflow-hidden  p-8">


                    {/* Title */}
                    <div className="mb-4 mt-4 text-center flex flex-col justify-center items-center">
                        <h1 className="text-2xl font-bold text-black mb-4 flex gap-2 items-center">
                            {/*<img src="/images/extlogo.png" className="size-6 object-cover" />*/}
                            You found a <span className="underline decoration-wavy decoration-orange-600 decoration-2 underline-offset-4">Learn+</span> feature
                        </h1>
                        {highlight && highlights[highlight] && <p className="text-sm text-gray-700">Go wild with <strong className="text-gray-800">{highlights[highlight]}</strong> plus all other Learn+ features!</p>}
                        {!highlight || !highlights[highlight] && <p className="text-sm text-gray-700">Go wild with all Learn+ features!</p>}
                        <p className="text-sm text-gray-700 mt-2">Here's a <strong className="text-orange-600">free 7-day trial</strong> - no strings attached</p>
                    </div>

                    {/* Buttons */}
                    <div className="space-y-1 mt-8 flex flex-col items-center justify-center bg-white relative z-1">
                        {/* Primary CTA */}
                        <button onClick={acceptTrial} className="relative group overflow-hidden bg-brand-gradient rounded-2xl w-2/3 mx-auto text-white py-3.5 px-6 font-semibold cursor-pointer">
                            Claim trial
                        </button>

                        {/* Secondary Button */}

                        <button onClick={scrollToComparison} className="w-full py-2.5 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg">
                            See what’s included in Learn+
                        </button>

                    </div>

                    <div className="absolute bottom-[-30%] left-[12.5%] w-[78%]">
                        <img src={startguy} className="w-full h-full object-cover" />
                    </div>
                </div>

                <div className="w-full p-4 pb-8" ref={scrollToRef}>
                    <p className="text-lg text-center ml-2 font-bold text-gray-700 my-8">See what’s included:</p>
                    <Comparison />
                </div>

            </div>

        </div>
    )
}


export const ClaimTrial = () => {

    const [searchParams] = useSearchParams();
    const highlight = searchParams.get("highlight");

    return (
        <CommunicationProvider>
            <ClaimTrialContent highlight={highlight} />
        </CommunicationProvider>
    )
}